// frontend/src/pages/Dashboard-IMPROVED.jsx
/**
 * 🚀 DASHBOARD ULTRA-OPTIMISÉ ET ENRICHI
 * 
 * NOUVELLES FONCTIONNALITÉS:
 * - Moyennes mensuelles et annuelles des revenus (calcul standard : total / mois écoulés, total / années écoulées)
 * - Moyenne par abonné et tarif moyen par kWh
 * - Pourcentages de répartition (ne dépassant pas 100%)
 * - Graphique avec toutes les adresses (sans limite)
 * - Export Impact Environnemental complet, filtrable par Village + Mois
 * - Filtre Mois + Village pour la section "Répartition des Compteurs par Adresse"
 *   avec export PDF/Excel ciblé
 */
import React, { useState, useMemo } from 'react';
import { dashboardService } from '../services/api';
import { 
  Zap, Users, Receipt, DollarSign, TrendingUp, TrendingDown,
  Activity, AlertTriangle, CheckCircle, Clock, Package, Battery, Leaf, RefreshCw,
  Download, Calendar, TrendingDown as TrendDown,FileSpreadsheet, FileText, MapPin, Droplets
} from 'lucide-react';

import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, Area, AreaChart, ComposedChart
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { useCachedApi } from '../hooks/useCachedApi';
import { PERMISSIONS } from '../utils/permissions';

const MOIS_OPTIONS = [
  { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' },
];

const StatCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 rounded-lg bg-${color}-50 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
      {trend !== undefined && trend !== 0 && (
        <span className={`text-sm font-medium flex items-center ${
          trend > 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {trend > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
          {Math.abs(trend).toFixed(1)}%
        </span>
      )}
    </div>
    <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

// ✅ Carte Compteurs : total du parc, puis Actifs | Bloqués sur une ligne
// (Bloqués = statut "suspended" + statut "blocked")
const MetersStatCard = ({ total, active, blocked }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
        <Zap className="w-6 h-6 text-blue-600" />
      </div>
    </div>
    <h3 className="text-gray-600 text-sm font-medium mb-1">Total Compteurs</h3>
    <p className="text-2xl font-bold text-gray-900">{total || 0}</p>
    <p className="text-sm text-gray-600 mt-2">
      Actifs : <span className="font-semibold text-green-600">{active || 0}</span>
      <span className="mx-3 text-gray-300">|</span>
      Bloqués : <span className="font-semibold text-red-600">{blocked || 0}</span>
    </p>
  </div>
);

// 💧⚡ Configuration d'affichage par type de compteur
const UTILITY_OPTIONS = [
  { value: 'all', label: 'Tous', icon: null },
  { value: 'electricity', label: 'Électricité', icon: Zap },
  { value: 'water', label: 'Eau', icon: Droplets },
];

const UtilitySelector = ({ utility, setUtility }) => (
  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
    {UTILITY_OPTIONS.map(({ value, label, icon: Icon }) => {
      const active = utility === value;
      const activeClass = value === 'water'
        ? 'bg-cyan-600 text-white shadow'
        : value === 'electricity'
          ? 'bg-amber-500 text-white shadow'
          : 'bg-gray-800 text-white shadow';
      return (
        <button
          key={value}
          onClick={() => setUtility(value)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            active ? activeClass : 'text-gray-700 hover:bg-gray-200'
          }`}
        >
          {Icon && <Icon className="w-4 h-4" />}
          {label}
        </button>
      );
    })}
  </div>
);

// 💧⚡ Carte : nombre de compteurs Eau / Électricité
const UtilityMetersCard = ({ type, total, active, blocked, percentage, customers, revenue, todayRevenue, volume, todayVolume, avgPrice, formatCurrency }) => {
  const isWater = type === 'water';
  const Icon = isWater ? Droplets : Zap;
  const unit = isWater ? 'm³' : 'kWh';
  const fmtVol = (v) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: isWater ? 3 : 2 }).format(v || 0) + ' ' + unit;
  const theme = isWater
    ? { bg: 'from-cyan-50 to-sky-100', border: 'border-cyan-200', icon: 'bg-cyan-600', text: 'text-cyan-900', sub: 'text-cyan-600', bar: 'bg-cyan-500' }
    : { bg: 'from-amber-50 to-yellow-100', border: 'border-amber-200', icon: 'bg-amber-500', text: 'text-amber-900', sub: 'text-amber-700', bar: 'bg-amber-500' };

  return (
    <div className={`bg-gradient-to-br ${theme.bg} rounded-xl border ${theme.border} p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg ${theme.icon} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className={`text-lg font-bold ${theme.text}`}>
              {isWater ? 'Compteurs Eau' : 'Compteurs Électricité'}
            </h3>
            <p className={`text-xs ${theme.sub}`}>
              {customers || 0} abonné{(customers || 0) > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-extrabold ${theme.text}`}>{total || 0}</p>
          <p className={`text-xs ${theme.sub}`}>{(percentage || 0).toFixed(1)}% du parc</p>
        </div>
      </div>

      <div className="w-full bg-white/70 rounded-full h-2 mb-4">
        <div className={`${theme.bar} h-2 rounded-full`} style={{ width: `${Math.min(100, percentage || 0)}%` }} />
      </div>

      <div className="flex items-center text-sm text-gray-700 mb-4">
        Actifs : <span className="font-semibold text-green-600 ml-1">{active || 0}</span>
        <span className="mx-3 text-gray-300">|</span>
        Bloqués : <span className="font-semibold text-red-600 ml-1">{blocked || 0}</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/80 rounded-lg p-3">
          <p className="text-xs text-gray-500">Revenu total</p>
          <p className="text-base font-bold text-gray-900">{formatCurrency(revenue || 0)}</p>
          <p className="text-xs text-gray-500">Auj. : {formatCurrency(todayRevenue || 0)}</p>
        </div>
        <div className="bg-white/80 rounded-lg p-3">
          <p className="text-xs text-gray-500">{isWater ? 'Volume vendu' : 'Énergie vendue'}</p>
          <p className="text-base font-bold text-gray-900">{fmtVol(volume)}</p>
          <p className="text-xs text-gray-500">Auj. : {fmtVol(todayVolume)}</p>
        </div>
        <div className="bg-white/80 rounded-lg p-3 col-span-2 flex items-center justify-between">
          <p className="text-xs text-gray-500">Prix moyen</p>
          <p className="text-sm font-bold text-gray-900">
            {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(avgPrice || 0)} FCFA/{unit}
          </p>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { hasPermission } = usePermissions();
  const [period, setPeriod] = useState('7days');
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportingEnvironmental, setExportingEnvironmental] = useState(false);

  // 🚀 UNE SEULE REQUÊTE pour TOUT le dashboard
  const { data: dashboardData, loading, refresh } = useCachedApi(
    () => dashboardService.getSummary(period),
    [period, refreshKey],
    { cacheDuration: 60000, cacheKey: `dashboard-summary-${period}-${refreshKey}` }
  );

  // 💧⚡ Graphiques : Tous / Électricité / Eau
  const [utility, setUtility] = useState('all');
  const { data: utilityCharts, loading: loadingUtilityCharts } = useCachedApi(
    () => dashboardService.getUtilityCharts(period, utility),
    [period, utility, refreshKey],
    { cacheDuration: 60000, cacheKey: `dashboard-utility-${period}-${utility}-${refreshKey}` }
  );

  const stats = dashboardData?.stats || {};
  const charts = dashboardData?.charts || {};
  const recentTransactions = dashboardData?.recent_transactions || [];
  const topCustomers = dashboardData?.top_customers || [];
  const metersByDepartement = dashboardData?.meters_by_departement || [];
  const metersByAddressAll = dashboardData?.meters_by_address || [];
  const [exportingVillages, setExportingVillages] = useState(null);

  // ✅ Filtre Mois / Village pour la section "Répartition des Compteurs par Adresse"
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(null); // null = pas de filtre mois (vue globale)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedAddress, setSelectedAddress] = useState('all'); // 'all' = tous les villages

  // Requête filtrée par mois (uniquement si un mois est sélectionné)
  const shouldFilterByMonth = selectedMonth !== null;
  const { data: filteredAddressData, loading: loadingFiltered } = useCachedApi(
    () => shouldFilterByMonth
      ? dashboardService.getMetersByAddress({ month: selectedMonth, year: selectedYear })
      : Promise.resolve(null),
    [selectedMonth, selectedYear, shouldFilterByMonth],
    { cacheDuration: 60000, cacheKey: `meters-by-address-${selectedMonth}-${selectedYear}` }
  );

  // Données affichées dans le graphique/la liste : filtrées par mois si actif, sinon vue globale
  const metersByAddressDisplay = shouldFilterByMonth
    ? (filteredAddressData || [])
    : metersByAddressAll;

  // Liste des villages disponibles pour les sélecteurs (basée sur la vue globale, toujours complète)
  // ✅ Une adresse n'apparaît qu'une fois (le backend regroupe déjà
  // majuscules/minuscules/accents ; ceci est une sécurité côté affichage).
  const villageOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const v of metersByAddressAll) {
      const key = (v.address || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      list.push(v.address);
    }
    return list.sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [metersByAddressAll]);

  // Applique en plus le filtre village (uniquement côté affichage)
  const metersByAddressFinal = selectedAddress === 'all'
    ? metersByAddressDisplay
    : metersByAddressDisplay.filter(v => v.address === selectedAddress);

  // Le village choisi, isolé (pour affichage détaillé) — visible dès qu'un
  // village est sélectionné, avec ou sans filtre de mois ("Tous les mois" inclus)
  const singleVillageDetail = selectedAddress !== 'all'
    ? metersByAddressFinal[0]
    : null;

  // ✅ Filtre village dédié à la carte "Compteurs par Secteur" (Ménage/ASER/Moulin/Autre)
  const [professionAddress, setProfessionAddress] = useState('all'); // 'all' = tout le parc
  const shouldFilterProfession = professionAddress !== 'all';
  const { data: professionData, loading: loadingProfession } = useCachedApi(
    () => shouldFilterProfession
      ? dashboardService.getMetersByProfession({ address: professionAddress })
      : Promise.resolve(null),
    [professionAddress, shouldFilterProfession],
    { cacheDuration: 60000, cacheKey: `meters-by-profession-${professionAddress}` }
  );
  const professionBreakdown = shouldFilterProfession
    ? (professionData?.breakdown || [])
    : (stats?.meters_by_profession || []);

  const chartData = utilityCharts?.revenue || charts.revenue || [];
  const volumeData = utility === 'all'
    ? (utilityCharts?.revenue || [])
    : (utilityCharts?.volume || []);
  const utilityTotals = utilityCharts?.totals || {};
  const volumeUnit = utility === 'water' ? 'm³' : 'kWh';
  const co2Data = charts.co2 || [];

  // ✅ Filtre Mois / Village dédié à la carte "Impact Environnemental"
  const [envMonth, setEnvMonth] = useState(null); // null = tous les mois
  const [envYear, setEnvYear] = useState(now.getFullYear());
  const [envAddress, setEnvAddress] = useState('all'); // 'all' = tous les villages

  const shouldFilterEnv = envMonth !== null || envAddress !== 'all';

// ✅ On appelle toujours l'endpoint dédié, même sans filtre, pour avoir le CO2
// évité total pour TOUS les villages et TOUS les mois (indépendant du
// sélecteur de période 7j/30j/12 mois en haut de page).
const { data: envSummary, loading: loadingEnvSummary } = useCachedApi(
  () => dashboardService.getEnvironmentalImpactSummary({
    month: envMonth ?? undefined,
    year: envMonth !== null ? envYear : undefined,
    address: envAddress !== 'all' ? envAddress : undefined,
  }),
  [envMonth, envYear, envAddress],
  { cacheDuration: 60000, cacheKey: `env-impact-${envMonth}-${envYear}-${envAddress}` }
);

// Stats affichées : toujours issues de l'endpoint dédié à l'impact
// environnemental.
const envStats = envSummary || {};

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // ✅ Export Impact Environnemental — respecte le filtre Mois / Village en cours
  const handleExportEnvironmental = async () => {
    try {
      setExportingEnvironmental(true);

      const params = {};
      if (envMonth !== null) {
        params.month = envMonth;
        params.year = envYear;
      }
      if (envAddress !== 'all') {
        params.address = envAddress;
      }

      const response = await dashboardService.exportEnvironmentalImpactXlsx(params);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute(
        'download',
        `impact_environnemental_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert("Export Excel de l'impact environnemental réussi !");
    } catch (error) {
      console.error('Erreur export environnemental:', error);
      alert("Erreur lors de l'export Excel");
    } finally {
      setExportingEnvironmental(false);
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mb-3" />
        <span className="text-gray-600">
          Chargement du tableau de bord...
        </span>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatChartDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      if (period === '7days' || period === 'month') {
        return format(date, 'dd MMM', { locale: fr });
      } else {
        return format(date, 'MMM yyyy', { locale: fr });
      }
    } catch {
      return dateStr;
    }
  };

  const formatKwh = (kwh) => {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(kwh) + ' kWh';
  };

  const formatM3 = (m3) => {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 }).format(m3 || 0) + ' m³';
  };

  const formatVolume = (v, unit = volumeUnit) => (unit === 'm³' ? formatM3(v) : formatKwh(v || 0));

  const formatCO2Tonnes = (tonnes) => {
    if (tonnes < 0.001) {
      return `${(tonnes * 1000).toFixed(2)} kg`;
    }
    return `${tonnes.toFixed(3)} t`;
  };

  const PeriodSelector = ({ period, setPeriod }) => (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setPeriod('7days')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          period === '7days'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        7 Jours
      </button>
      <button
        onClick={() => setPeriod('month')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          period === 'month'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        30 Jours
      </button>
      <button
        onClick={() => setPeriod('year')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          period === 'year'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        12 Mois
      </button>
    </div>
  );

const handleExportVillagesXlsx = async () => {
  try {
    setExportingVillages('xlsx');
    const params = {};
    if (shouldFilterByMonth) {
      params.month = selectedMonth;
      params.year = selectedYear;
    }
    if (selectedAddress !== 'all') {
      params.address = selectedAddress;
    }
    const response = await dashboardService.exportVillagesXlsx(params);
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `villages_stats_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
    alert("Erreur export Excel");
  } finally {
    setExportingVillages(null);
  }
};

const handleExportVillagesPdf = async () => {
  try {
    setExportingVillages('pdf');
    const params = {};
    if (shouldFilterByMonth) {
      params.month = selectedMonth;
      params.year = selectedYear;
    }
    if (selectedAddress !== 'all') {
      params.address = selectedAddress;
    }
    const response = await dashboardService.exportVillagesPdf(params);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `villages_stats_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
    alert("Erreur export PDF");
  } finally {
    setExportingVillages(null);
  }
};
  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tableau de Bord</h1>
            <p className="text-blue-100 mt-2">
              Vue d'ensemble de votre activité - {format(new Date(), 'EEEE dd MMMM yyyy', { locale: fr })}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
          <MetersStatCard
            total={stats?.total_meters || 0}
            active={stats?.active_meters || 0}
            blocked={stats?.blocked_meters ?? stats?.suspended_meters ?? 0}
          />
          <StatCard
            title="Total Clients"
            value={stats?.total_customers || 0}
            icon={Users}
            color="green"
          />
          <StatCard
            title="Transactions"
            value={stats?.total_transactions || 0}
            icon={Receipt}
            color="purple"
            subtitle={`${stats?.pending_transactions || 0} en attente`}
          />
          <StatCard
            title="Revenu Total"
            value={formatCurrency(stats?.total_revenue || 0)}
            icon={DollarSign}
            color="yellow"
            trend={stats?.revenue_trend}
            subtitle={`Aujourd'hui: ${formatCurrency(stats?.today_revenue || 0)}`}
          />
           <StatCard
            title="Énergie Totale (électricité)"
            value={formatKwh(stats?.total_kwh || 0)}
            icon={Battery}
            color="purple"
            subtitle={`Aujourd'hui: ${formatKwh(stats?.today_kwh || 0)}`}
          />
          <StatCard
            title="Eau Totale Vendue"
            value={formatM3(stats?.total_m3 || 0)}
            icon={Droplets}
            color="cyan"
            subtitle={`Aujourd'hui: ${formatM3(stats?.today_m3 || 0)}`}
          />
          <StatCard
            title="CO₂ Évité"
            value={`${(stats?.total_co2_avoided_tonnes ?? (stats?.total_co2_avoided_kg || 0) / 1000).toFixed(2)} t`}
            icon={Leaf}
            color="green"
            subtitle={`Aujourd'hui: ${formatCO2Tonnes(stats?.today_co2_avoided_tonnes || 0)}`}
          />
        </PermissionGuard>
      </div>

      {/* 💧⚡ Compteurs Eau / Électricité */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UtilityMetersCard
            type="electricity"
            total={stats?.electricity_meters}
            active={stats?.electricity_active_meters}
            blocked={stats?.electricity_blocked_meters}
            percentage={stats?.electricity_percentage}
            customers={stats?.electricity_customers}
            revenue={stats?.electricity_revenue}
            todayRevenue={stats?.electricity_today_revenue}
            volume={stats?.total_kwh}
            todayVolume={stats?.today_kwh}
            avgPrice={stats?.avg_price_per_kwh}
            formatCurrency={formatCurrency}
          />
          <UtilityMetersCard
            type="water"
            total={stats?.water_meters}
            active={stats?.water_active_meters}
            blocked={stats?.water_blocked_meters}
            percentage={stats?.water_percentage}
            customers={stats?.water_customers}
            revenue={stats?.water_revenue}
            todayRevenue={stats?.water_today_revenue}
            volume={stats?.total_m3}
            todayVolume={stats?.today_m3}
            avgPrice={stats?.avg_price_per_m3}
            formatCurrency={formatCurrency}
          />
        </div>
      </PermissionGuard>

      {/* ✅ CARTES: MOYENNES (calcul standard : total / mois écoulés, total / années écoulées) */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Moyennes et Statistiques</h2>
              <p className="text-sm text-gray-600">Indicateurs de performance</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Moyenne Mensuelle</span>
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-700">
                {formatCurrency(stats?.monthly_avg_revenue || 0)}
              </p>
              <p className="text-xs text-blue-600 mt-1">Revenus/mois</p>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-indigo-900">Moyenne Annuelle</span>
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <p className="text-2xl font-bold text-indigo-700">
                {formatCurrency(stats?.annual_avg_revenue || 0)}
              </p>
              <p className="text-xs text-indigo-600 mt-1">Revenus/an</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-900">Moyenne par Abonné/Mois</span>
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-700">
                {formatCurrency(stats?.monthly_avg_per_customer || 0)}
              </p>
              <p className="text-xs text-green-600 mt-1">Revenu du mois / nb abonnés</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-900">kWh Mensuel Moyen</span>
                <Battery className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-700">
                {formatKwh(stats?.monthly_avg_kwh || 0)}
              </p>
              <p className="text-xs text-purple-600 mt-1">Électricité/mois · Eau : {formatM3(stats?.monthly_avg_m3 || 0)}/mois</p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-yellow-900">Tarif Moyen/kWh</span>
                <DollarSign className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-yellow-700">
                {stats?.avg_price_per_kwh?.toFixed(0) || 0}
              </p>
              <p className="text-xs text-yellow-600 mt-1">FCFA/kWh · Eau : {(stats?.avg_price_per_m3 || 0).toFixed(0)} FCFA/m³</p>
            </div>
          </div>
        </div>
      </PermissionGuard>

      {/* Compteurs par Marque avec pourcentages */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600">Compteurs Stron</h3>
                  <p className="text-2xl font-bold text-gray-900">{stats?.stron_meters || 0}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Pourcentage</p>
                <p className="text-lg font-semibold text-indigo-600">
                  {stats?.stron_percentage?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(stats?.stron_percentage || 0, 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600">Compteurs Gomelong</h3>
                  <p className="text-2xl font-bold text-gray-900">{stats?.gomelong_meters || 0}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Pourcentage</p>
                <p className="text-lg font-semibold text-orange-600">
                  {stats?.gomelong_percentage?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(stats?.gomelong_percentage || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </PermissionGuard>

      {/* ✅ Compteurs par Secteur (profession) — Ménage, ASER, Moulin, Autre... chacun isolé avec son pourcentage */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Compteurs par Secteur</h2>
                <p className="text-sm text-gray-600">
                  {shouldFilterProfession
                    ? `Répartition pour ${professionAddress}`
                    : 'Répartition par secteur (Ménage, ASER, Moulin, Autre...)'}
                </p>
              </div>
            </div>

            {/* ✅ Sélecteur de village */}
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              <select
                value={professionAddress}
                onChange={(e) => setProfessionAddress(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 max-w-[220px]"
              >
                <option value="all">Tous les villages</option>
                {villageOptions.map(addr => (
                  <option key={addr} value={addr}>{addr}</option>
                ))}
              </select>
              {loadingProfession && shouldFilterProfession && (
                <RefreshCw className="w-4 h-4 animate-spin text-teal-600" />
              )}
            </div>
          </div>

          {(loadingProfession && shouldFilterProfession) ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
            </div>
          ) : (!professionBreakdown || professionBreakdown.length === 0) ? (
            <p className="text-sm text-gray-500 text-center py-4">
              {shouldFilterProfession ? 'Aucun compteur pour ce village' : 'Aucune donnée de secteur disponible'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {professionBreakdown.map((p, idx) => {
                const palette = [
                  { bg: 'bg-indigo-100', bar: 'bg-indigo-600', text: 'text-indigo-600' },
                  { bg: 'bg-orange-100', bar: 'bg-orange-600', text: 'text-orange-600' },
                  { bg: 'bg-teal-100',   bar: 'bg-teal-600',   text: 'text-teal-600' },
                  { bg: 'bg-pink-100',   bar: 'bg-pink-600',   text: 'text-pink-600' },
                  { bg: 'bg-gray-100',   bar: 'bg-gray-500',   text: 'text-gray-600' },
                ];
                const c = palette[idx % palette.length];
                return (
                  <div key={p.profession} className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center`}>
                          <Package className={`w-5 h-5 ${c.text}`} />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-600">{p.label}</h3>
                          <p className="text-xl font-bold text-gray-900">{p.total}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Pourcentage</p>
                        <p className={`text-lg font-semibold ${c.text}`}>{p.percentage?.toFixed(1) || 0}%</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`${c.bar} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${Math.min(p.percentage || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PermissionGuard>

      {/* Alertes Rapides */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Impayés</p>
                <p className="text-2xl font-bold text-red-700">
                  {formatCurrency(stats?.total_unpaid_fees || 0)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Alertes Non Lues</p>
                <p className="text-2xl font-bold text-yellow-700">{stats?.unread_alerts || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Taux de Collecte</p>
                <p className="text-2xl font-bold text-green-700">
                  {Math.min(stats?.collection_rate || 0, 100).toFixed(1)}%
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>
      </PermissionGuard>

      {/* Impact Environnemental avec Export + Filtre Village/Mois */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm border-2 border-green-200 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-600 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-green-900">Impact Environnemental</h2>
                <p className="text-sm text-green-700">
                  CO₂ évité grâce aux centrales photovoltaïques vs gasoil/fioul
                  {shouldFilterEnv && (
                    <>
                      {' — '}
                      {envAddress !== 'all' ? envAddress : 'Tous les villages'}
                      {envMonth !== null && ` · ${MOIS_OPTIONS.find(m => m.value === envMonth)?.label} ${envYear}`}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* ✅ BOUTON EXPORT */}
            <button
              onClick={handleExportEnvironmental}
              disabled={exportingEnvironmental}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {exportingEnvironmental ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Exporter Excel
            </button>
          </div>

          {/* ✅ Sélecteurs Mois / Village pour l'impact environnemental */}
          <div className="flex items-center gap-3 flex-wrap bg-white/60 border border-green-200 rounded-lg p-3 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={envMonth ?? ''}
                onChange={(e) => setEnvMonth(e.target.value === '' ? null : Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Tous les mois</option>
                {MOIS_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {envMonth !== null && (
              <select
                value={envYear}
                onChange={(e) => setEnvYear(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}

            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              <select
                value={envAddress}
                onChange={(e) => setEnvAddress(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 max-w-[220px]"
              >
                <option value="all">Tous les villages</option>
                {villageOptions.map(addr => (
                  <option key={addr} value={addr}>{addr}</option>
                ))}
              </select>
            </div>

            {shouldFilterEnv && (
              <button
                onClick={() => { setEnvMonth(null); setEnvAddress('all'); }}
                className="text-sm text-green-700 hover:text-green-900 font-medium"
              >
                Réinitialiser
              </button>
            )}

           {loadingEnvSummary && (
  <RefreshCw className="w-4 h-4 animate-spin text-green-600" />
)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">CO₂ Évité Total</span>
                <Leaf className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-700">
                {envStats?.total_co2_avoided_tonnes 
                  ? `${envStats.total_co2_avoided_tonnes.toFixed(2)} t`
                  : '0 t'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {(envStats?.total_co2_avoided_kg || 0).toFixed(0)} kg évités
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Gasoil Évité</span>
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                </svg>
              </div>
              <p className="text-2xl font-bold text-green-700">
                {Math.round(envStats?.diesel_liters_avoided || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">litres économisés</p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Équivalent Arbres</span>
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z"/>
                </svg>
              </div>
              <p className="text-2xl font-bold text-green-700">
                {Math.round(envStats?.trees_equivalent || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                arbres/an (absorption)
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Voitures Évitées</span>
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                </svg>
              </div>
              <p className="text-2xl font-bold text-green-700">
                {envStats?.cars_equivalent?.toFixed(1) || '0'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                émissions/an
              </p>
            </div>
          </div>
          
          <div className="mt-4 bg-green-100 border border-green-300 rounded-lg p-4">
            <p className="text-sm text-green-800 text-center">
              <span className="font-bold">🌍 Impact Positif :</span> 
              {' '}Sans nos centrales photovoltaïques, {Math.round(envStats?.diesel_liters_avoided || 0).toLocaleString()} litres 
              de gasoil/fioul auraient été brûlés, émettant {(envStats?.total_co2_avoided_tonnes || 0).toFixed(2)} tonnes de CO₂
              {shouldFilterEnv ? (
                <>
                  {' '}pour {envAddress !== 'all' ? envAddress : 'l\'ensemble des villages'}
                  {envMonth !== null && ` en ${MOIS_OPTIONS.find(m => m.value === envMonth)?.label} ${envYear}`} !
                </>
              ) : ' !'}
            </p>
          </div>
        </div>
      </PermissionGuard>

      {/* Graphique Revenus avec pourcentages */}
      <PermissionGuard permission={PERMISSIONS.VIEW_CHARTS}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Évolution des Revenus
                {utility === 'water' && ' — 💧 Eau'}
                {utility === 'electricity' && ' — ⚡ Électricité'}
              </h2>
              {loadingUtilityCharts && (
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Chargement...
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <UtilitySelector utility={utility} setUtility={setUtility} />
              <PeriodSelector period={period} setPeriod={setPeriod} />
            </div>
          </div>

          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData} barGap={4} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={formatChartDate}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => {
                  if (typeof value === 'number' && name !== 'percentage') return formatCurrency(value);
                  if (name === 'percentage') return `${value.toFixed(1)}%`;
                  return value;
                }}
                labelFormatter={(date) => {
                  try {
                    return format(parseISO(date), 'dd MMMM yyyy', { locale: fr });
                  } catch {
                    return date;
                  }
                }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              />
              <Legend />
              {utility === 'all' ? (
                <>
                  {/* ✅ Barres côte à côte (non empilées) */}
                  <Bar dataKey="electricity_revenue" name="⚡ Électricité" fill="#f59e0b" radius={[6, 6, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="water_revenue" name="💧 Eau" fill="#06b6d4" radius={[6, 6, 0, 0]} maxBarSize={40} />
                </>
              ) : (
                <Bar
                  dataKey="revenue"
                  name={utility === 'water' ? 'Revenu Eau' : 'Revenu Électricité'}
                  fill={utility === 'water' ? '#06b6d4' : '#f59e0b'}
                  radius={[8, 8, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-600 font-medium mb-1">Revenu de la période</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(utilityTotals.revenue || 0)}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <p className="text-xs text-amber-700 font-medium mb-1">⚡ Électricité</p>
              <p className="text-lg font-bold text-amber-900">{formatCurrency(utilityTotals.electricity_revenue || 0)}</p>
            </div>
            <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
              <p className="text-xs text-cyan-700 font-medium mb-1">💧 Eau</p>
              <p className="text-lg font-bold text-cyan-900">{formatCurrency(utilityTotals.water_revenue || 0)}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <p className="text-xs text-purple-700 font-medium mb-1">Transactions</p>
              <p className="text-lg font-bold text-purple-900">{utilityTotals.transaction_count || 0}</p>
            </div>
          </div>
        </div>
      </PermissionGuard>

      {/* Graphique kWh */}
      <PermissionGuard permission={PERMISSIONS.VIEW_CHARTS}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {utility === 'water' && "Évolution de l'Eau Achetée (m³)"}
              {utility === 'electricity' && "Évolution de l'Énergie Achetée (kWh)"}
              {utility === 'all' && 'Volumes Achetés — kWh et m³'}
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <UtilitySelector utility={utility} setUtility={setUtility} />
              <PeriodSelector period={period} setPeriod={setPeriod} />
            </div>
          </div>

          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={volumeData} barGap={4} barCategoryGap="20%">
              <defs>
                <linearGradient id="colorKwh" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorM3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={formatChartDate}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              {utility === 'all' && (
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              )}
              <Tooltip
                formatter={(value, name, item) => {
                  const key = item?.dataKey;
                  if (key === 'total_m3') return formatM3(value);
                  if (key === 'total_kwh') return formatKwh(value);
                  return formatVolume(value);
                }}
                labelFormatter={(date) => {
                  try {
                    return format(parseISO(date), 'dd MMMM yyyy', { locale: fr });
                  } catch {
                    return date;
                  }
                }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              />
              <Legend />
              {utility === 'all' ? (
                <>
                  {/* ✅ Barres côte à côte : kWh (axe gauche) et m³ (axe droit) */}
                  <Bar
                    yAxisId="left"
                    dataKey="total_kwh"
                    name="⚡ Énergie (kWh)"
                    fill="#8b5cf6"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={40}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="total_m3"
                    name="💧 Eau (m³)"
                    fill="#06b6d4"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={40}
                  />
                </>
              ) : (
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="volume"
                  name={utility === 'water' ? '💧 Eau (m³)' : '⚡ Énergie (kWh)'}
                  stroke={utility === 'water' ? '#06b6d4' : '#8b5cf6'}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill={utility === 'water' ? 'url(#colorM3)' : 'url(#colorKwh)'}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {utility !== 'water' && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <p className="text-xs text-purple-700 font-medium mb-1">⚡ Énergie de la période</p>
                <p className="text-lg font-bold text-purple-900">{formatKwh(utilityTotals.kwh || 0)}</p>
              </div>
            )}
            {utility !== 'electricity' && (
              <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                <p className="text-xs text-cyan-700 font-medium mb-1">💧 Eau de la période</p>
                <p className="text-lg font-bold text-cyan-900">{formatM3(utilityTotals.m3 || 0)}</p>
              </div>
            )}
            {utility !== 'all' && (
              <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                <p className="text-xs text-yellow-700 font-medium mb-1">Prix moyen de la période</p>
                <p className="text-lg font-bold text-yellow-900">
                  {(utilityTotals.avg_price_per_unit || 0).toFixed(0)} FCFA/{volumeUnit}
                </p>
              </div>
            )}
          </div>
        </div>
      </PermissionGuard>

      {/* Graphique CO2 */}
      <PermissionGuard permission={PERMISSIONS.VIEW_CHARTS}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Évolution du CO₂ Évité
                </h2>
                <p className="text-sm text-gray-600">Impact environnemental cumulatif (⚡ électricité uniquement)</p>
              </div>
            </div>
            <PeriodSelector period={period} setPeriod={setPeriod} />
          </div>

          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={co2Data}>
              <defs>
                <linearGradient id="colorCo2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={formatChartDate}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'co2_avoided_tonnes') {
                    return [`${value.toFixed(3)} tonnes`, 'CO₂ évité'];
                  }
                  if (name === 'cumulative_co2_tonnes') {
                    return [`${value.toFixed(2)} tonnes`, 'Total cumulé'];
                  }
                  return [value, name];
                }}
                labelFormatter={(date) => {
                  try {
                    return format(parseISO(date), 'dd MMMM yyyy', { locale: fr });
                  } catch {
                    return date;
                  }
                }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="co2_avoided_tonnes"
                name="CO₂ évité (tonnes)"
                stroke="#10b981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorCo2)"
              />
              <Line
                type="monotone"
                dataKey="cumulative_co2_tonnes"
                name="Total cumulé"
                stroke="#059669"
                strokeWidth={3}
                dot={{ fill: '#059669', r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-700 font-medium mb-1">Total période</p>
              <p className="text-xl font-bold text-green-900">
                {co2Data.length > 0 
                  ? `${co2Data[co2Data.length - 1]?.cumulative_co2_tonnes?.toFixed(2) || 0} t`
                  : '0 t'}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-700 font-medium mb-1">Moyenne journalière</p>
              <p className="text-xl font-bold text-green-900">
                {co2Data.length > 0
                  ? `${(co2Data.reduce((sum, d) => sum + (d.co2_avoided_tonnes || 0), 0) / co2Data.length).toFixed(3)} t`
                  : '0 t'}
              </p>
            </div>
          </div>
        </div>
      </PermissionGuard>

      {/* ✅ Compteurs par Adresse - avec filtre Mois + Village */}
      <PermissionGuard permission={PERMISSIONS.VIEW_STATS}>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Répartition des Compteurs par Adresse
                </h2>
                <span className="text-sm text-gray-600">
                  {metersByAddressFinal.length} adresse(s) affichée(s)
                  {shouldFilterByMonth && ` — ${MOIS_OPTIONS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportVillagesXlsx}
                  disabled={exportingVillages === 'xlsx'}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {exportingVillages === 'xlsx'
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <FileSpreadsheet className="w-3.5 h-3.5" />}
                  Excel
                </button>
                <button
                  onClick={handleExportVillagesPdf}
                  disabled={exportingVillages === 'pdf'}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {exportingVillages === 'pdf'
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <FileText className="w-3.5 h-3.5" />}
                  PDF
                </button>
              </div>
            </div>

            {/* ✅ Sélecteurs Mois / Village */}
            <div className="flex items-center gap-3 flex-wrap bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedMonth ?? ''}
                  onChange={(e) => setSelectedMonth(e.target.value === '' ? null : Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous les mois</option>
                  {MOIS_OPTIONS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {shouldFilterByMonth && (
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              )}

              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedAddress}
                  onChange={(e) => setSelectedAddress(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[220px]"
                >
                  <option value="all">Tous les villages</option>
                  {villageOptions.map(addr => (
                    <option key={addr} value={addr}>{addr}</option>
                  ))}
                </select>
              </div>

              {(shouldFilterByMonth || selectedAddress !== 'all') && (
                <button
                  onClick={() => { setSelectedMonth(null); setSelectedAddress('all'); }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>

          {/* ✅ Vue détail : un village choisi — sur toutes les périodes ou un mois précis */}
          {singleVillageDetail && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">
                {singleVillageDetail.address}
                {shouldFilterByMonth
                  ? ` — ${MOIS_OPTIONS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
                  : ' — Toutes les périodes'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-gray-500">Montant Acheté</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(singleVillageDetail.total_revenue)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-gray-500">kWh Total</p>
                  <p className="text-lg font-bold text-blue-700">{formatKwh(singleVillageDetail.total_kwh)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-gray-500">Transactions</p>
                  <p className="text-lg font-bold text-blue-700">{singleVillageDetail.transaction_count}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-gray-500">Nb Abonnés</p>
                  <p className="text-lg font-bold text-indigo-700">{singleVillageDetail.customer_count}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-gray-500">Compteurs Actifs</p>
                  <p className="text-lg font-bold text-green-700">{singleVillageDetail.active}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-gray-500">Compteurs Bloqués</p>
                  <p className="text-lg font-bold text-red-700">{singleVillageDetail.suspended}</p>
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-3">
                💡 {shouldFilterByMonth
                  ? 'Changez le mois ci-dessus pour comparer ce que ce village a acheté sur un autre mois.'
                  : 'Sélectionnez un mois ci-dessus pour voir le détail de ce village sur une période précise.'}
              </p>
            </div>
          )}

          {loadingFiltered && shouldFilterByMonth ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : metersByAddressFinal.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune donnée disponible pour cette sélection</p>
          ) : (
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <ResponsiveContainer width={Math.max(metersByAddressFinal.length * 80, 1200)} height={400}>
                <BarChart
                  data={metersByAddressFinal}
                  margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="address"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={120}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => {
                      const label = name === "active" ? "Actifs" : 
                                  name === "suspended" ? "Bloqués" : 
                                  name === "total" ? "Total" : name;
                      return [value, label];
                    }}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                  />

                  <Bar
                    dataKey="active"
                    fill="#10b981"
                    name="Actifs"
                    radius={[8, 8, 0, 0]}
                  />

                  <Bar
                    dataKey="suspended"
                    fill="#ef4444"
                    name="Bloqués"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-600 text-center">
              💡 Sélectionnez un mois et/ou un village pour filtrer, puis exportez en PDF ou Excel.
            </p>
          </div>
        </div>
      </PermissionGuard>

      {/* Recent Transactions */}
      <PermissionGuard permission={PERMISSIONS.VIEW_TRANSACTIONS}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Transactions Récentes
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID Transaction</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Montant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Méthode</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((txn) => (
                  <tr key={txn.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-mono text-gray-900">{txn.transaction_id}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{formatCurrency(txn.amount)}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 capitalize">{txn.payment_method}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        txn.status === 'completed' ? 'bg-green-100 text-green-700' :
                        txn.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {txn.status === 'completed' ? 'Complétée' : 
                         txn.status === 'pending' ? 'En attente' : 'Échouée'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">
                      {format(new Date(txn.created_at), 'dd/MM/yyyy HH:mm')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </PermissionGuard>
    </div>
  );
};

export default Dashboard;